import { invokeActionWithLifecycle } from "lib/action/invokeActionWithLifecycle";
import { useCallback } from "react";
import { useStore } from "jotai";
import componentStackAtom from "app/uiState/mobile/componentStackAtom";
import { getScreenRouteOwnerId } from "./screenRoute";
import { useQueryClient } from "@tanstack/react-query";
import { componentOptions } from "app/componentOptions";
import { JM_ENVIRONMENT } from "app/Firebase";
import { useIsMobileOrTablet, useOpenComponent } from "app/jm";
import Toaster from "app/Toaster";
import { useOpenOverlay } from "app/useOpenOverlay";
import {
  getPersistFalseComponentHref,
  type ComponentRouteResolutionResult,
} from "./componentRouteResolver";
import { useRoutedComponentStack } from "./currentRoutedComponent";
import {
  getMobileComponentStackHistoryState,
  getMobileComponentStackHistoryDepth,
  getMobileComponentStackRouteHref,
} from "./mobileComponentStackRoutes";
import { getRecordByTypeAndId } from "lib/api";
import { resolve } from "src/gqty";
import { resolveActionPermissions } from "lib/action/actionPermissions";

type ResolvedComponentRoute = Extract<
  ComponentRouteResolutionResult,
  { type: "component" }
>;
type ResolvedActionRoute = Extract<
  ComponentRouteResolutionResult,
  { type: "action" }
>;
type ResolvedRoute = ResolvedComponentRoute | ResolvedActionRoute;

type PreparedPayloadValue =
  | object
  | string
  | number
  | boolean
  | null
  | undefined;

type PreparedPayload = Record<string, PreparedPayloadValue>;

type OpenResolvedRouteComponentOptions = {
  resolution: ResolvedRoute;
  signature: string;
  onIgnoredEmptyRouteHref: (href: string) => void;
};

function getCurrentPathSearchHash() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function ensureMobileComponentRouteBackTarget(
  resolution: ResolvedComponentRoute,
) {
  if (resolution.persist === false) {
    return;
  }

  const currentHref = getCurrentPathSearchHash();
  const baseHref = getPersistFalseComponentHref(
    window.location.href,
    JM_ENVIRONMENT,
    resolution.tenantId,
  );

  if (
    currentHref === baseHref ||
    (getMobileComponentStackHistoryDepth(window.history.state) ?? 0) > 0 ||
    getScreenRouteOwnerId(window.history.state) !== null
  ) {
    return;
  }

  window.history.replaceState(
    getMobileComponentStackHistoryState(0),
    document.title,
    baseHref,
  );
  window.history.pushState(
    getMobileComponentStackHistoryState(1),
    document.title,
    currentHref,
  );
}

function cleanupPersistFalseRoute(
  resolution: ResolvedRoute,
  onIgnoredEmptyRouteHref: (href: string) => void,
) {
  if (resolution.persist !== false) {
    return;
  }

  const href = getPersistFalseComponentHref(
    window.location.href,
    JM_ENVIRONMENT,
    resolution.tenantId,
  );

  onIgnoredEmptyRouteHref(href);
  window.history.replaceState({}, document.title, href);
}

export function useRouteComponentNavigation() {
  const store = useStore();
  const openComponent = useOpenComponent();
  const openOverlay = useOpenOverlay();
  const queryClient = useQueryClient();
  const isMobileOrTablet = useIsMobileOrTablet();
  const { entries, setRouteComponent, clearRouteComponents } =
    useRoutedComponentStack();

  const clearRoutedComponents = useCallback(() => {
    if (entries.length > 0) {
      clearRouteComponents();
    }
  }, [clearRouteComponents, entries.length]);

  const isMobileComponentStackRoute = useCallback(() => {
    if (!isMobileOrTablet) return false;
    const state: unknown = window.history.state;
    const depth = getMobileComponentStackHistoryDepth(state);
    if (depth === null) return false;
    const stack = store.get(componentStackAtom);
    const ownerId = getScreenRouteOwnerId(state);
    if (ownerId !== null) {
      return stack.some((entry) => entry.controller.id === ownerId);
    }
    const entry = stack[depth - 1];
    if (!entry) return false;
    const componentState = store.get(entry.componentAtom);
    const tenantId = new URLSearchParams(window.location.search).get("tenant");
    return (
      getMobileComponentStackRouteHref(componentState, JM_ENVIRONMENT, tenantId) ===
      window.location.pathname + window.location.search
    );
  }, [isMobileOrTablet, store]);

  const executeResolvedRouteAction = useCallback(
    async (resolution: ResolvedActionRoute) => {
      const { action, actionId } = resolution;

      if (!action || !actionId) {
        Toaster.error("Unable to open this action from the link.");
        return;
      }

      let records: PreparedPayloadValue[] = [];

      if (resolution.recordId) {
        const recordId = resolution.recordId;
        const recordPayload = await resolve<PreparedPayload | null>(
          ({ query }) => {
            const record = getRecordByTypeAndId(
              query,
              resolution.recordType,
              recordId,
            );
            if (!record) {
              return null;
            }

            return {
              [actionId]: action.prepare ? action.prepare(record) : undefined,
            };
          },
        );

        if (!recordPayload || !(actionId in recordPayload)) {
          Toaster.error("Record not found.");
          return;
        }

        const preparedRecord = recordPayload[actionId];
        records = preparedRecord ? [preparedRecord] : [];
      }

      let authorization;
      try {
        authorization = await resolveActionPermissions(action, records);
      } catch {
        Toaster.error("Unable to check permissions for this shortcut.");
        return;
      }
      if (!authorization.permitted) {
        Toaster.error("You do not have permission to perform this action.");
        return;
      }

      const canInvoke = action.canInvoke
        ? await action.canInvoke({
            records,
            params: undefined,
            permissions: authorization.permissions,
          })
        : true;

      if (!canInvoke) {
        Toaster.error("This action cannot be invoked for the linked record.");
        return;
      }

      await invokeActionWithLifecycle(action, {
        openComponent,
        openOverlay,
        setLoading: () => {},
        cancel: () => {},
        success: ({ successMsg, silent } = {}) => {
          if (!silent && successMsg) {
            Toaster.success(successMsg);
          }
        },
        fail: ({ errorMsg, silent }) => {
          if (!silent) {
            Toaster.error(
              errorMsg || "An error occurred while opening this action.",
            );
          }
        },
        queryClient,
        records,
        params: undefined,
        permissions: authorization.permissions,
      });
    },
    [openComponent, openOverlay, queryClient],
  );

  const openResolvedRoute = useCallback(
    async ({
      resolution,
      signature,
      onIgnoredEmptyRouteHref,
    }: OpenResolvedRouteComponentOptions) => {
      if (resolution.type === "action") {
        cleanupPersistFalseRoute(resolution, onIgnoredEmptyRouteHref);
        await executeResolvedRouteAction(resolution);
        return;
      }

      if (isMobileOrTablet) {
        const { componentType, componentProps, uiProps } =
          resolution.componentState;

        ensureMobileComponentRouteBackTarget(resolution);
        setRouteComponent({
          componentId: `route-${signature}`,
          componentType,
          componentProps,
          uiProps: {
            ...componentOptions[componentType].defaultUiOptions,
            ...uiProps,
          },
        });
      } else {
        openComponent(resolution.componentState);
      }

      cleanupPersistFalseRoute(resolution, onIgnoredEmptyRouteHref);
    },
    [
      executeResolvedRouteAction,
      isMobileOrTablet,
      openComponent,
      setRouteComponent,
    ],
  );

  return {
    hasRoutedComponents: entries.length > 0,
    clearRoutedComponents,
    isMobileComponentStackRoute,
    openResolvedRoute,
  };
}
